import React, {
  createRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect
} from 'react';

import _toLower from 'lodash/toLower';
import _uniqueId from 'lodash/uniqueId';
import List, { ListItemBasic, ListItemText } from 'components/List';
import Menu, { MenuProps, MenuRef } from 'components/Menu';

const HIGHLIGHT_MARK_START = '<span style="font-family:NokiaPureBold">';
const HIGHLIGHT_MARK_END = '</span>';
const MARK_LENGTH = 47; // length of marking strings
const TEXT_TRUNCATE_STYLE = {
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const markItem = (item, searchPattern, truncateListText = true) => {
  if (!item) {
    return null;
  }
  if (!searchPattern || searchPattern.length === 0) {
    return item;
  }
  let endingIndex = 0;
  const searchPatternLength: number = searchPattern.length;
  let markedText = item;
  while (true) { // eslint-disable-line no-constant-condition
    const startIndex: number = _toLower(markedText).indexOf(_toLower(searchPattern), endingIndex);
    if (startIndex === -1) {
      break;
    }
    const actualText: string = markedText.substr(startIndex, searchPatternLength);
    const replacementText = `${HIGHLIGHT_MARK_START}${actualText}${HIGHLIGHT_MARK_END}`;
    const beginningText: string = startIndex > 0 ? markedText.slice(0, startIndex) : '';
    const endingText: string = markedText.slice(startIndex + searchPatternLength);
    markedText = beginningText + replacementText + endingText;
    endingIndex = startIndex + searchPatternLength + MARK_LENGTH;
  }
  return <div style={truncateListText ? TEXT_TRUNCATE_STYLE : {}} dangerouslySetInnerHTML={{ __html: markedText }} />;
};

const MENU_CONFIG = {
  usePortal: true,
  positioningOptions: {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [-16, 10]
        }
      }
    ]
  }
};

export interface SearchwChipsListProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'contextMenu'> {
  /**
   * List data
   * `event`: The event toggling the menu, it is used to find the position of the pointer, omit this to render the list under the item.
   * `items`: List items, in addition to <ListItemBasic /> props it includes
   * `items.itemNode`: To override the list item
   * `items.key`: The key of the item (it is required if `itemNode` is not provided)
   * `items.content`: The content of the list item, instead of <ListItemBasic /> children prop (it is required if `itemNode` is not provided)
   * `items.searchPattern`: The inserted value in the corresponding field. It is used for highlighting the searched string when content is provided as string.
   * `items.onClick`: The onClick handler of the list item.
   */
  list?: {
    event?: {},
    items?: {
      disabled?: boolean,
      itemNode?: React.ReactNode,
      key?: string,
      content?: string | React.ReactNode,
      searchPattern?: string,
      onClick?: React.MouseEventHandler
    }[]
  };
  /** The element that list is connected to */
  triggerEl?: React.RefObject<HTMLElement>;
  /** The ref of the List wrapper */
  searchWChipsListRef?: React.RefObject<HTMLElement>;
  /** To config the position container */
  menuProps?: Partial<MenuProps>;
}

export type SearchwChipsListHandle = {
  positionMenu(any):void,
  forcePositionMenu(any):void
};


/** <SearchwChipsList/> is used to render the contextual menu (copy and paste) and autosuggest list.
 * To re-position menu on changes to children or target element, please use `positionMenu` or `forcePositionMenu` callbacks using component ref.
 */
const SearchwChipsList = forwardRef<SearchwChipsListHandle, SearchwChipsListProps>((props: SearchwChipsListProps, ref) => {
  const {
    list,
    triggerEl,
    searchWChipsListRef,
    menuProps,
    ...otherProps
  } = props;
  if (!list) {
    return null;
  }
  const {
    event,
    items,
    ...otherListProps
  } = list;
  let virtualEl;
  const menuRef = createRef<MenuRef>();
  const [menuKey, setKey] = useState(_uniqueId());
  const forcePositionMenu = () => { setKey(_uniqueId()); };
  useImperativeHandle(ref, () => ({
    positionMenu: () => {
      menuRef.current && menuRef.current.positionMenu();
    },
    forcePositionMenu
  }));

  useEffect(() => {
    forcePositionMenu();
  }, [items.length]);

  // ContextualMenu should be rendered next to the pointer so a virtual target is used
  if (event && (event as MouseEvent).type === 'contextmenu') {
    const {
      pageX,
      pageY
    } = (event as MouseEvent);
    virtualEl = {
      current: {
        contains: () => false,
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          top: pageY,
          right: pageX,
          bottom: pageY,
          left: pageX,
        }),
      }
    };
  }
  return (
    <Menu
      key={menuKey}
      ref={menuRef}
      {...{
        isOpen: true,
        triggerEl: virtualEl || triggerEl,
        ...MENU_CONFIG,
        ...menuProps
      }}
    >
      <List
        ref={searchWChipsListRef}
        style={{
          maxHeight: '50vh'
        }}
        {...otherProps}
        {...otherListProps}
      >
        {items.map(({
          itemNode,
          key,
          content,
          searchPattern,
          disabled,
          ...listItemBasicProps
        }) => (
          itemNode ||
          <ListItemBasic
            key={key}
            disabled={disabled}
            {...listItemBasicProps}
          >
            {typeof content === 'string'
              ? (
                <ListItemText>
                  {searchPattern !== undefined && !disabled ? markItem(content, searchPattern) : content}
                </ListItemText>)
              : content}
          </ListItemBasic>
        ))}
      </List>
    </Menu>
  );
});

SearchwChipsList.defaultProps = {
  list: undefined,
  triggerEl: {
    current: undefined
  },
  searchWChipsListRef: {
    current: undefined
  },
  menuProps: {}
};
SearchwChipsList.displayName = 'SearchwChipsList';
export default SearchwChipsList;
